import { HTTPStatusError } from './chat-api';
import { buildAgentFileURL } from '@/utils/agent-file-url';

const BOOK_MEDIA_LIMITS = {
  cover: 10 * 1024 * 1024,
  video: 100 * 1024 * 1024,
} as const;

export type BookSourceRef = {
  type?: string;
  agent_id?: string;
  site_name?: string;
  owner_id?: string;
  source_url?: string;
};

export type BookSubmission = {
  id: string;
  source_ref: BookSourceRef;
  source_url: string;
  title: string;
  caption: string;
  creator_display_name: string;
  creator_avatar_url: string;
  cover_asset_url: string;
  preview_assets?: Record<string, unknown>;
  preview_frame_asset_urls?: string[] | null;
  public_url?: string;
  status: string;
  like_count: number;
  changed_since_review?: boolean;
};

export type BookSiteCandidate = {
  id: string;
  owner_user_id: string;
  owner_display_name?: string;
  owner_email?: string;
  owner_avatar_url?: string;
  agent_id: string;
  site_name: string;
  source_url: string;
  title: string;
  created_at?: string;
  already_in_book: boolean;
  submission_id?: string;
  submission_status?: string;
};

export type BookPreviewInput = {
  cover_asset_url?: string;
  preview_frame_asset_urls?: string[];
  preview_assets?: Record<string, unknown>;
};

export type ListBookSubmissionsResponse = {
  submissions: BookSubmission[];
  items?: BookSubmission[];
  has_more?: boolean;
  limit: number;
  offset: number;
};

export type ListBookSiteCandidatesResponse = {
  candidates: BookSiteCandidate[];
  has_more?: boolean;
  limit: number;
  offset: number;
};

export type AgentBookDetail = {
  book: AgentBook;
  artifacts: AgentBookArtifactDetail[];
};

export type AgentBook = {
  book_id: string;
  name: string;
  path: string;
  description?: string | null;
  status: string;
  created_from_session_id?: string | null;
  last_session_id?: string | null;
  created_at: number;
  updated_at: number;
};

export type AgentBookArtifactDetail = {
  artifact: AgentBookArtifact;
  versions: AgentBookVersionDetail[];
};

export type AgentBookArtifact = {
  artifact_id: string;
  book_id: string;
  book_path?: string | null;
  artifact_key: string;
  title: string;
  kind?: string | null;
  category?: string | null;
  current_version_id?: string | null;
  latest_version_id?: string | null;
  created_at: number;
  updated_at: number;
};

export type AgentBookVersionDetail = {
  version: AgentBookVersion;
  files: AgentBookVersionFile[];
  session_links: AgentBookVersionSessionLink[];
};

export type AgentBookVersion = {
  version_id: string;
  artifact_id: string;
  book_id: string;
  base_version_id: string;
  version_number: number;
  label: string;
  status: string;
  immutable_root_path: string;
  immutable_file_path?: string | null;
  source_path?: string | null;
  file_name?: string | null;
  mime_type?: string | null;
  size_bytes: number;
  preview_ref?: string | null;
  thumbnail_ref?: string | null;
  publish_status?: string | null;
  published_url?: string | null;
  source_prompt?: string | null;
  change_summary?: string | null;
  created_by_message_id?: number | null;
  created_by_tool_call_id?: string | null;
  source_session_id?: string | null;
  delivery_request_id?: string | null;
  created_at: number;
};

export type AgentBookVersionFile = {
  version_id?: string | null;
  relative_path: string;
  vfs_path: string;
  mime_type?: string | null;
  size_bytes: number;
  content_hash?: string | null;
};

export type AgentBookVersionSessionLink = {
  book_id: string;
  artifact_id: string;
  version_id: string;
  session_id: string;
  relation: string;
  first_message_id: number;
  last_message_id: number;
  created_at: number;
  updated_at: number;
};

export type ProjectBook = {
  id: string;
  artifactID: string;
  title: string;
  project: ProjectBookProject;
  versions: ProjectBookVersion[];
};

export type ProjectBookProject = {
  id: string;
  name: string;
  path: string;
  description: string;
  createdFromSessionID: string;
  lastSessionID: string;
  lastActiveAt: number;
};

export type ProjectBookVersionSessionLink = {
  bookID: string;
  artifactID: string;
  versionID: string;
  sessionID: string;
  relation: string;
  firstMessageID: number;
  lastMessageID: number;
  createdAt: number;
  updatedAt: number;
};

export type ProjectBookVersion = {
  id: string;
  baseVersionID: string;
  sourceVersionIDs: string[];
  label: string;
  versionNumber: number;
  title: string;
  kind: string;
  mimeType: string;
  fileName: string;
  filesRootPath: string;
  sourcePath: string;
  sourceSessionID?: string | null;
  createdByMessageID?: number | null;
  createdAt: number;
  publishStatus: string;
  publishedURL: string;
  sessionLinks: ProjectBookVersionSessionLink[];
  files: ProjectBookFile[];
};

export type ProjectBookFile = {
  id: string;
  path: string;
  size: number;
  modifiedAt: number;
};

async function readErrorBody(response: Response): Promise<unknown> {
  const contentType = response.headers.get('content-type') ?? '';
  if (contentType.includes('application/json')) {
    return response.json().catch(() => null);
  }
  return response.text().catch(() => null);
}

function buildErrorMessage(status: number, body: unknown): string {
  if (
    typeof body === 'object' &&
    body !== null &&
    'error' in body &&
    typeof body.error === 'object' &&
    body.error !== null &&
    'message' in body.error &&
    typeof body.error.message === 'string'
  ) {
    return body.error.message;
  }
  if (typeof body === 'string' && body.trim()) {
    return body.trim();
  }
  return `Request failed (${status})`;
}

async function bookJSON<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    credentials: 'include',
    ...init,
  });
  if (!response.ok) {
    const body = await readErrorBody(response);
    throw new HTTPStatusError(
      response.status,
      buildErrorMessage(response.status, body)
    );
  }
  return (await response.json()) as T;
}

function formatBytes(bytes: number): string {
  const mb = bytes / (1024 * 1024);
  if (mb >= 10 || Number.isInteger(mb)) {
    return `${Math.round(mb)}MB`;
  }
  return `${mb.toFixed(1)}MB`;
}

function assertBookMediaUpload(file: File, type: 'cover' | 'video') {
  const limit = BOOK_MEDIA_LIMITS[type];
  if (file.size > limit) {
    const label = type === 'cover' ? 'Cover image' : 'Cover video';
    throw new Error(
      `${label} must be ${formatBytes(limit)} or smaller. This file is ${formatBytes(file.size)}.`
    );
  }
}

export async function listBookSubmissions(
  limit = 50,
  offset = 0
): Promise<ListBookSubmissionsResponse> {
  return bookJSON(
    `/v1/user/gallery/submissions?limit=${limit}&offset=${offset}`
  );
}

export async function listBookSiteCandidates(
  limit = 50,
  offset = 0
): Promise<ListBookSiteCandidatesResponse> {
  return bookJSON(
    `/v1/user/gallery/site-candidates?limit=${limit}&offset=${offset}`
  );
}

export async function listAgentBooks(limit = 100): Promise<AgentBookDetail[]> {
  const body = await bookJSON<{ books: AgentBookDetail[] }>(
    `/v1/user/agent/books?limit=${limit}`
  );
  return body.books ?? [];
}

export async function readAgentBookFileText(
  filePath: string,
  byteLimit = 64 * 1024
): Promise<string> {
  const response = await fetch(buildAgentFileURL(filePath), {
    credentials: 'include',
    headers: {
      accept: 'text/html,text/plain,application/json,text/*,*/*',
    },
  });
  if (!response.ok) {
    const body = await readErrorBody(response);
    throw new HTTPStatusError(
      response.status,
      buildErrorMessage(response.status, body)
    );
  }

  const data = await response.arrayBuffer();
  return new TextDecoder().decode(data.slice(0, byteLimit));
}

export function projectBooksFromAgentBooks(
  details: AgentBookDetail[]
): ProjectBook[] {
  return details
    .flatMap(detail => {
      const project = {
        id: detail.book.book_id,
        name: detail.book.name,
        path: detail.book.path,
        description: detail.book.description ?? '',
        createdFromSessionID: detail.book.created_from_session_id ?? '',
        lastSessionID: detail.book.last_session_id ?? '',
        lastActiveAt: detail.book.updated_at,
      };
      const versions = aggregateProjectVersions(
        project,
        projectVersionsFromDetail(detail)
      );
      if (versions.length === 0) {
        return [];
      }
      return [
        {
          id: detail.book.book_id,
          artifactID: detail.book.book_id,
          title: nonEmptyString(detail.book.name) ?? detail.book.book_id,
          project,
          versions,
        },
      ];
    })
    .sort(
      (left, right) =>
        getProjectBookUpdatedAt(right) - getProjectBookUpdatedAt(left)
    );
}

function projectVersionsFromDetail(
  detail: AgentBookDetail
): ProjectBookVersion[] {
  return detail.artifacts.flatMap(artifactDetail =>
    artifactDetail.versions.flatMap(versionDetail => {
      const { version } = versionDetail;
      const filesRootPath = version.immutable_root_path
        ? `${version.immutable_root_path}/files`
        : detail.book.path;
      const files = versionDetail.files.map(file => ({
        id: file.vfs_path,
        path: file.vfs_path,
        size: file.size_bytes,
        modifiedAt: version.created_at,
      }));
      if (files.length === 0) {
        return [];
      }

      const primaryFile = versionDetail.files[0];
      const primaryVFSFileName = primaryFile?.vfs_path
        ? lastPathComponent(primaryFile.vfs_path)
        : '';
      const fileName =
        nonEmptyString(version.file_name ?? '') ??
        nonEmptyString(primaryFile?.relative_path ?? '') ??
        nonEmptyString(primaryVFSFileName) ??
        '';

      return [
        {
          id: version.version_id,
          baseVersionID: version.base_version_id,
          sourceVersionIDs: [version.version_id],
          label: nonEmptyString(version.label) ?? `v${version.version_number}`,
          versionNumber: version.version_number,
          title: artifactTitle(artifactDetail.artifact, version, fileName),
          kind: artifactKind(artifactDetail.artifact, version, files),
          mimeType:
            nonEmptyString(version.mime_type ?? '') ??
            primaryFile?.mime_type ??
            '',
          fileName,
          filesRootPath,
          sourcePath: version.source_path ?? '',
          sourceSessionID: version.source_session_id ?? null,
          createdByMessageID:
            version.created_by_message_id && version.created_by_message_id > 0
              ? version.created_by_message_id
              : null,
          createdAt: version.created_at,
          publishStatus: version.publish_status ?? '',
          publishedURL: version.published_url ?? '',
          sessionLinks: versionDetail.session_links.map(link => ({
            bookID: link.book_id,
            artifactID: link.artifact_id,
            versionID: link.version_id,
            sessionID: link.session_id,
            relation: link.relation,
            firstMessageID: link.first_message_id,
            lastMessageID: link.last_message_id,
            createdAt: link.created_at,
            updatedAt: link.updated_at,
          })),
          files,
        },
      ];
    })
  );
}

function aggregateProjectVersions(
  project: ProjectBookProject,
  artifactVersions: ProjectBookVersion[]
): ProjectBookVersion[] {
  const versionsWithFiles = artifactVersions.filter(
    version => version.files.length > 0
  );
  if (versionsWithFiles.length === 0) {
    return [];
  }

  let fallbackGroupKey = -1;
  const grouped = new Map<number, ProjectBookVersion[]>();
  for (const version of versionsWithFiles) {
    let key: number;
    if (version.createdByMessageID && version.createdByMessageID > 0) {
      key = version.createdByMessageID;
    } else if (version.createdAt > 0) {
      key = version.createdAt;
    } else {
      key = fallbackGroupKey;
      fallbackGroupKey -= 1;
    }
    grouped.set(key, [...(grouped.get(key) ?? []), version]);
  }

  const sortedGroups = [...grouped.values()].sort(
    (left, right) =>
      Math.max(...left.map(getBookVersionModifiedAt)) -
      Math.max(...right.map(getBookVersionModifiedAt))
  );

  return sortedGroups.map((group, index) => {
    const sortedGroup = [...group].sort((left, right) =>
      left.title.localeCompare(right.title, undefined, { sensitivity: 'base' })
    );
    const files = uniqueFilesByPath(
      sortedGroup.flatMap(version => version.files)
    ).sort((left, right) =>
      getBookFileName(left).localeCompare(getBookFileName(right), undefined, {
        sensitivity: 'base',
      })
    );
    const createdAt = Math.min(
      ...sortedGroup.map(version => version.createdAt)
    );
    const sourcePaths = sortedGroup
      .map(version => version.sourcePath)
      .filter(Boolean);
    const sourceVersionIDs = uniqueStrings(
      sortedGroup.flatMap(version => version.sourceVersionIDs)
    );
    const sessionLinks = uniqueSessionLinks(
      sortedGroup.flatMap(version => version.sessionLinks)
    );

    return {
      id: `${project.id}:project:v${index + 1}`,
      baseVersionID: firstNonEmptyString(
        sortedGroup.map(version => version.baseVersionID)
      ),
      sourceVersionIDs,
      label: sortedGroups.length === 1 ? 'Latest version' : `v${index + 1}`,
      versionNumber: index + 1,
      title: project.name,
      kind: 'project',
      mimeType: '',
      fileName: '',
      filesRootPath: project.path,
      sourcePath: sourcePaths.join('\n'),
      sourceSessionID: [...sortedGroup]
        .reverse()
        .find(version => Boolean(version.sourceSessionID))?.sourceSessionID,
      createdByMessageID:
        sortedGroup
          .map(version => version.createdByMessageID ?? 0)
          .filter(value => value > 0)
          .sort((left, right) => right - left)[0] ?? null,
      createdAt,
      publishStatus: firstNonEmptyString(
        sortedGroup.map(version => version.publishStatus)
      ),
      publishedURL: firstNonEmptyString(
        sortedGroup.map(version => version.publishedURL)
      ),
      sessionLinks,
      files,
    };
  });
}

export function getProjectBookDisplayTitle(book: ProjectBook): string {
  return nonEmptyString(book.project.name) ?? book.title;
}

export function getProjectBookLatestVersion(
  book: ProjectBook
): ProjectBookVersion | null {
  return book.versions.at(-1) ?? null;
}

export function getProjectBookLastChangedAt(book: ProjectBook): number {
  return (
    getBookVersionModifiedAt(getProjectBookLatestVersion(book)) ||
    book.project.lastActiveAt
  );
}

export function getProjectBookUpdatedAt(book: ProjectBook): number {
  return Math.max(
    book.project.lastActiveAt,
    getBookVersionModifiedAt(getProjectBookLatestVersion(book))
  );
}

export function getBookVersionModifiedAt(
  version: ProjectBookVersion | null | undefined
): number {
  if (!version) {
    return 0;
  }
  return Math.max(
    version.createdAt,
    ...version.files.map(file => file.modifiedAt)
  );
}

export function isBookVersionWebsite(
  version: ProjectBookVersion | null | undefined
): boolean {
  if (!version) {
    return false;
  }
  const normalized = version.kind.trim().toLowerCase();
  return (
    normalized === 'website' ||
    version.files.some(file => file.path.endsWith('/index.html'))
  );
}

export function getPrimaryBookFile(
  version: ProjectBookVersion | null | undefined
): ProjectBookFile | null {
  if (!version) {
    return null;
  }
  return (
    version.files.find(file => file.path.endsWith('/index.html')) ??
    version.files.find(isPreviewableTextFile) ??
    version.files[0] ??
    null
  );
}

export function getBookVersionArtifactKindLabel(
  version: ProjectBookVersion | null | undefined
): string {
  const explicit = explicitKindLabel(version?.kind ?? '');
  if (explicit) {
    return explicit;
  }
  if (isBookVersionWebsite(version)) {
    return 'HTML';
  }
  const primaryFile = getPrimaryBookFile(version);
  return primaryFile ? getBookFileKindLabel(primaryFile) : 'Files';
}

export function getBookVersionArtifactSystemIcon(
  version: ProjectBookVersion | null | undefined
): string {
  if (explicitKindLabel(version?.kind ?? '') === 'Project') {
    return 'grid';
  }
  if (isBookVersionWebsite(version)) {
    return 'globe';
  }
  const primaryFile = getPrimaryBookFile(version);
  return primaryFile ? getBookFileIconKind(primaryFile) : 'folder';
}

export function getBookFileName(file: ProjectBookFile): string {
  return lastPathComponent(file.path) || 'artifact-file';
}

export function getBookFileExtension(file: ProjectBookFile): string {
  const name = getBookFileName(file);
  const index = name.lastIndexOf('.');
  return index >= 0 ? name.slice(index + 1).toLowerCase() : '';
}

export function isPreviewableTextFile(file: ProjectBookFile): boolean {
  return [
    'md',
    'txt',
    'json',
    'html',
    'css',
    'js',
    'ts',
    'tsx',
    'jsx',
  ].includes(getBookFileExtension(file));
}

export function isCoverPreviewableFile(file: ProjectBookFile): boolean {
  return ['pdf', 'png', 'jpg', 'jpeg', 'gif', 'webp', 'heic'].includes(
    getBookFileExtension(file)
  );
}

export function getBookFileKindLabel(file: ProjectBookFile): string {
  switch (getBookFileExtension(file)) {
    case 'pdf':
      return 'PDF';
    case 'png':
    case 'jpg':
    case 'jpeg':
    case 'gif':
    case 'webp':
    case 'heic':
      return 'Image';
    case 'zip':
    case 'tar':
    case 'gz':
      return 'Archive';
    case 'doc':
    case 'docx':
      return 'Word';
    case 'xls':
    case 'xlsx':
    case 'csv':
      return 'Spreadsheet';
    case 'ppt':
    case 'pptx':
      return 'Slides';
    case 'md':
      return 'Markdown';
    case 'html':
    case 'htm':
      return 'HTML';
    case 'txt':
      return 'Text';
    default:
      return 'File';
  }
}

export function getBookFileIconKind(file: ProjectBookFile): string {
  switch (getBookFileExtension(file)) {
    case 'pdf':
      return 'file-text';
    case 'png':
    case 'jpg':
    case 'jpeg':
    case 'gif':
    case 'webp':
    case 'heic':
      return 'image';
    case 'zip':
    case 'tar':
    case 'gz':
      return 'archive';
    case 'xls':
    case 'xlsx':
    case 'csv':
      return 'table';
    case 'ppt':
    case 'pptx':
      return 'panels';
    case 'md':
    case 'txt':
    case 'json':
    case 'html':
    case 'css':
    case 'js':
    case 'ts':
    case 'tsx':
    case 'jsx':
      return 'file-text';
    default:
      return 'file';
  }
}

function artifactTitle(
  artifact: AgentBookArtifact,
  version: AgentBookVersion,
  fileName: string
): string {
  return (
    nonEmptyString(artifact.title) ??
    nonEmptyString(version.file_name ?? '') ??
    nonEmptyString(fileName) ??
    artifact.artifact_key
  );
}

function artifactKind(
  artifact: AgentBookArtifact,
  version: AgentBookVersion,
  files: ProjectBookFile[]
): string {
  const kind = nonEmptyString(artifact.kind ?? '');
  if (kind) {
    return kind;
  }
  const category = nonEmptyString(artifact.category ?? '');
  if (category) {
    return category;
  }
  if (version.mime_type?.toLowerCase().includes('html')) {
    return 'html';
  }
  return files[0] ? getBookFileKindLabel(files[0]).toLowerCase() : '';
}

function explicitKindLabel(kind: string): string | null {
  switch (kind.trim().toLowerCase()) {
    case 'project':
      return 'Project';
    case 'html':
    case 'htm':
    case 'website':
      return 'HTML';
    case 'pdf':
      return 'PDF';
    case 'markdown':
    case 'md':
      return 'Markdown';
    case 'text':
    case 'txt':
      return 'Text';
    case 'image':
    case 'png':
    case 'jpg':
    case 'jpeg':
    case 'gif':
    case 'webp':
    case 'heic':
      return 'Image';
    case 'video':
    case 'mp4':
    case 'mov':
    case 'webm':
      return 'Video';
    case 'audio':
    case 'mp3':
    case 'wav':
    case 'm4a':
      return 'Audio';
    case 'archive':
    case 'zip':
    case 'tar':
    case 'gz':
      return 'Archive';
    case '':
      return null;
    default:
      return kind
        .trim()
        .toLowerCase()
        .replace(/\b\w/g, value => value.toUpperCase());
  }
}

function uniqueFilesByPath(files: ProjectBookFile[]): ProjectBookFile[] {
  const seen = new Set<string>();
  const result: ProjectBookFile[] = [];
  for (const file of files) {
    if (seen.has(file.path)) {
      continue;
    }
    seen.add(file.path);
    result.push(file);
  }
  return result;
}

function uniqueStrings(values: string[]): string[] {
  return [...new Set(values.filter(Boolean))];
}

function uniqueSessionLinks(
  links: ProjectBookVersionSessionLink[]
): ProjectBookVersionSessionLink[] {
  const seen = new Set<string>();
  const result: ProjectBookVersionSessionLink[] = [];
  for (const link of links) {
    const key = `${link.sessionID}:${link.relation}`;
    if (seen.has(key)) {
      continue;
    }
    seen.add(key);
    result.push(link);
  }
  return result;
}

function firstNonEmptyString(values: string[]): string {
  return [...values].reverse().find(value => value.trim()) ?? '';
}

function nonEmptyString(value: string): string | null {
  const trimmed = value.trim();
  return trimmed ? trimmed : null;
}

function lastPathComponent(value: string): string {
  return (
    value
      .split('/')
      .reverse()
      .find(segment => segment.trim()) ?? ''
  );
}

export async function uploadBookCandidateMedia(
  file: File,
  type: 'cover' | 'video'
): Promise<{ status: string; path: string; url: string }> {
  assertBookMediaUpload(file, type);
  const formData = new FormData();
  formData.append('file', file);
  return bookJSON(`/v1/user/gallery/site-candidates/media?type=${type}`, {
    method: 'POST',
    body: formData,
  });
}

export async function uploadBookSubmissionMedia(
  id: string,
  file: File,
  type: 'cover' | 'video'
): Promise<{
  status: string;
  path: string;
  url: string;
  submission: BookSubmission;
}> {
  assertBookMediaUpload(file, type);
  const formData = new FormData();
  formData.append('file', file);
  return bookJSON(`/v1/user/gallery/submissions/${id}/media?type=${type}`, {
    method: 'POST',
    body: formData,
  });
}

export async function publishBookSiteCandidate(
  candidate: Pick<BookSiteCandidate, 'agent_id' | 'site_name'>,
  preview: BookPreviewInput
): Promise<BookSubmission> {
  const body = await bookJSON<{ submission: BookSubmission }>(
    '/v1/user/gallery/site-candidates/publish',
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        agent_id: candidate.agent_id,
        site_name: candidate.site_name,
        cover_asset_url: preview.cover_asset_url,
        preview_frame_asset_urls: preview.preview_frame_asset_urls ?? [],
        preview_assets: preview.preview_assets ?? {},
      }),
    }
  );
  return body.submission;
}

export async function renameBookSubmission(
  id: string,
  title: string
): Promise<BookSubmission> {
  const body = await bookJSON<{ submission: BookSubmission }>(
    `/v1/user/gallery/submissions/${id}`,
    {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ title }),
    }
  );
  return body.submission;
}

function submissionAction(
  id: string,
  action: 'hide' | 'restore' | 'delete',
  reason: string
): Promise<BookSubmission> {
  const method = action === 'delete' ? 'DELETE' : 'POST';
  return bookJSON<{ submission: BookSubmission }>(
    `/v1/user/gallery/submissions/${id}${action === 'delete' ? '' : `/${action}`}`,
    {
      method,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ reason }),
    }
  ).then(body => body.submission);
}

export function hideBookSubmission(id: string): Promise<BookSubmission> {
  return submissionAction(id, 'hide', 'owner hidden from Book manager');
}

export function restoreBookSubmission(id: string): Promise<BookSubmission> {
  return submissionAction(id, 'restore', 'owner restored from Book manager');
}

export function deleteBookSubmission(id: string): Promise<BookSubmission> {
  return submissionAction(id, 'delete', 'owner deleted from Book manager');
}
